import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { applyMessagePatch } from '../lib/optimisticMessage';

import { setFaviconSource } from '../components/gui/faviconBadge';

configure({ enforceActions: 'always' });

class MessageStore extends LocalModel {
    constructor() {
        super('message', {
            namePlural: 'messages',
            namePluralReal: 'messages',
            sort: 'createdDate',
            limit: 500,
            api: {
                search: {
                    url: '/api/messages/',
                    params: {
                        limit: 50,
                        sort: 'lastname',
                    },
                },
                load: {
                    url: '/api/messages/',
                    params: {},
                },
                save: {
                    url: '/api/messages/',
                    params: {},
                },
                delete: {
                    url: '/api/messages/',
                    params: {},
                },
            },
        });
    }

    @observable showAddMessage = {};

    @observable newMessage = {};

    @observable messageUuidv4 = null;
    @observable unread = 0;

    @observable message = {};

    @observable messages = [];

    @observable messagesTotal = 0;

    @observable search = null;
    @observable users = [];
    @observable conversationsUsers = [];
    @observable currentUser = {};

    @observable messageContent = '';

    @observable messageSearchResults = [];

    @observable aiResponse = {};
    @observable aiResponseObject = {};

    cleanupAi() {
        this.updateKeyValue('aiResponse', {});
        this.updateKeyValue('aiResponseObject', {});
    }

    async queryAi(message, field) {
        this.cleanupAi();
        const response = await util.fetchApi(`/api/ai/message`, { method: 'POST' }, {
            message,
        });
        switch (response?.status) {
            case 200: {
                this.updateKeyValue('aiResponse', response?.data);
                let responseObject = null;
                if (response?.data?.output_text) {
                    try {
                        responseObject = JSON.parse(response?.data?.output_text);
                    } catch (e) {
                        // Not JSON, do nothing
                    }
                }
                if (responseObject) {
                    this.updateKeyValue('aiResponseObject', responseObject);
                } else {
                    this.updateKeyValue('aiResponseObject', { [field]: response?.data?.output_text });
                }
                return response?.data;
            }
        }
    }

    async searchMessages(q) {
        if (!q || q.trim().length < 2) {
            this.updateKeyValue('messageSearchResults', []);
            return [];
        }
        const response = await util.fetchApi('/api/messages/search', { method: 'GET' }, { q });
        if (response.status === 200) {
            this.updateKeyValue('messageSearchResults', response.data || []);
            return response.data;
        }
        return [];
    }

    async searchForUsers(search) {
        const response = await util.fetchApi(`/api/messages/users/`, { method: 'GET' }, {
            search,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('users', response.data);
        }
    }

    @action
    cleanupConversation(skipMessages = false) {
        if (!skipMessages) {
            this.updateKeyValue('messages', []);
            this.updateKeyValue('messagesTotal', 0);
            this.updateKeyValue('currentUser', {});
        }
        this.updateKeyValue('conversationsUsers', []);
        this.updateKeyValue('users', []);
        this.updateKeyValue('message', {});
        this.updateKeyValue('messageUuidv4', null);
        this.updateKeyValue('messageContent', '');
    }

    // Optimistic UI helpers. updateKeyValue is an @action (localModel), and we
    // always replace the array rather than mutate it in place.
    addOptimisticMessage(message) {
        this.updateKeyValue('messages', [...this.messages, message]);
    }

    patchMessageByUuid(uuidv4, patch) {
        this.updateKeyValue('messages', applyMessagePatch(this.messages, uuidv4, patch));
    }

    async getConversationList() {
        const response = await util.fetchApi(`/api/messages/`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('conversationsUsers', response.data);
                // this.updateKeyValue('messages', response.included?.messages || []);
        }
    }

    async getConversation(uuidv4, limit = 10, offset = 0, onlyAfterLastMessage = false) {
        if (!uuidv4) {
            return;
        }
        let lastMessageDate = null;
        // console.log('onlyAfterLastMessage', onlyAfterLastMessage);
        // console.log('this.messages', this.messages);
        if (onlyAfterLastMessage && this.messages.length > 0) {
            // Find the most recent message date
            const sortedMessages = this.messages.slice().sort((a, b) => new Date(b.sentDate) - new Date(a.sentDate));
            // console.log('sortedMessages', sortedMessages);
            lastMessageDate = sortedMessages[0].sentDate;
            // console.log('lastMessageDate', lastMessageDate);
        }

        const response = await util.fetchApi(`/api/messages/${uuidv4}`, { method: 'GET' }, {
            limit,
            offset,
            lastMessageDate,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('messages', response.data);
                // Backend returns the FULL message count for the conversation as
                // included.hasMoreMessages (misnamed — it's a count, not a boolean).
                // We use it to know when there are older messages left to load.
                this.updateKeyValue('messagesTotal', response.included?.hasMoreMessages || 0);
                this.updateKeyValue('currentUser', response.included?.currentUser || {});
        }
    }

    async sendMessage(toUserUuidv4, body, { tempUuid, editUuidv4, images = [], replyToUuidv4 = null, limit } = {}) {
        // editUuidv4 is passed explicitly by the optimistic drawer. Legacy callers
        // (inbox/index.js) pass neither and rely on this.messageUuidv4 being the
        // edit target — preserve that. NOTE: we no longer clear messageContent here
        // (the caller clears its own input up front; clearing here would wipe the
        // next message the user has already started typing after an optimistic send).
        const editTarget = editUuidv4 !== undefined ? editUuidv4 : this.messageUuidv4;
        const markFailed = () => {
            const failUuid = editUuidv4 || tempUuid;
            if (failUuid) {
                this.patchMessageByUuid(failUuid, { pending: false, failed: true });
            }
        };
        try {
            const response = await util.fetchApi(`/api/messages/${toUserUuidv4}/new`, { method: 'POST' }, {
                toUser: toUserUuidv4,
                body,
                messageUuidv4: editTarget,
                images,
                replyToUuidv4,
            });
            if (response?.status === 201) {
                if (editUuidv4 === undefined) {
                    // Legacy caller relies on the store resetting the edit target.
                    this.updateKeyValue('messageUuidv4', null);
                }
                // Refetch the SAME window the caller is showing (the drawer passes
                // its loadedLimit) so a send never collapses the view back to the
                // default page size. limit undefined → getConversation default.
                await this.getConversation(toUserUuidv4, limit);
                return response.data;
            }
            markFailed();
            return response;
        } catch (err) {
            markFailed();
            return null;
        }
    }

    async sendMessageToAll(body, { tempUuid, replyToUuidv4 = null, images = [], limit } = {}) {
        const markFailed = () => {
            if (tempUuid) {
                this.patchMessageByUuid(tempUuid, { pending: false, failed: true });
            }
        };
        try {
            const response = await util.fetchApi(`/api/messages/broadcast/all`, { method: 'POST' }, {
                body,
                replyToUuidv4,
                images,
            });
            if (response?.status === 201) {
                await this.getConversation('@all', limit);
                return response.data;
            }
            markFailed();
            return response;
        } catch (err) {
            markFailed();
            return null;
        }
    }

    async deleteMessage(toUserUuidv4, messageId) {
        if (!messageId) {
            return;
        }
        const response = await util.fetchApi(`/api/messages/${messageId}`, { method: 'DELETE' });
        switch (response.status) {
            case 200:
                await this.getConversation(toUserUuidv4);
                return response.data;
        }
    }

    // limit is passed by the inbox drawer so the refetch keeps the currently-loaded
    // window (e.g. 50 after "load more") instead of collapsing back to the default.
    // Legacy callers omit it → getConversation falls back to its own default.
    async markMessageRead(fromUserUuidv4, messageUuidv4, limit) {
        if (!messageUuidv4) {
            return;
        }
        if (!fromUserUuidv4) {
            return;
        }
        const response = await util.fetchApi(`/api/messages/${fromUserUuidv4}/markasread/${messageUuidv4}`, { method: 'GET' });
        switch (response.status) {
            case 200:
                await this.getConversation(fromUserUuidv4, limit, 0);
                // Update unread count immediately after marking as read
                await this.getUnreadCount();
                return response.data;
        }
    }

    // Marks EVERY unread message in the conversation as read in one backend call
    // (including older messages not currently loaded), then refreshes the loaded
    // window and the unread badge. limit keeps the current window size.
    async markAllConversationRead(fromUserUuidv4, limit) {
        if (!fromUserUuidv4) {
            return;
        }
        const response = await util.fetchApi(`/api/messages/${fromUserUuidv4}/markallread`, { method: 'GET' });
        switch (response.status) {
            case 202:
                await this.getConversation(fromUserUuidv4, limit, 0);
                await this.getUnreadCount();
                return response.data;
        }
    }

    async getUnreadCount() {
        const response = await util.fetchApi(`/api/messages/unread`, { method: 'GET' });
        switch (response.status) {
            case 200: {
                const newUnreadCount = response.data || 0;
                setFaviconSource('inbox', newUnreadCount);
                this.updateKeyValue('unread', newUnreadCount);

                return newUnreadCount;
            }
        }
    }

}

const store = new MessageStore();
export default store;

// Named export for the class itself
export { MessageStore };
